import ProgramCardAccordion from '@/components/ProgramCardAccordion/ProgramCardAccordion'
import useUtilityPrograms, {
  isDeviceAllowedInProgram,
} from '@/app/hooks/useUtilityPrograms'
import { MultiProgramDeviceFlowProvider } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { Card, LoadingAnimation, Text } from '@/components'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import ProgramApplicationCard from '@/components/ProgramCard/ProgramApplicationCard'
import {
  DeviceType,
  DeviceUrlSlug,
} from '../DeviceSelectionGallery/DeviceSelectionGallery.types'
import { useParams } from 'react-router-dom'
import { deviceTypeFromUrlSlug } from '../DeviceSelectionGallery/DeviceSelectionGallery.utils'
import {
  getDeviceIneligiblityMessages,
  MappedUtilityProgram,
  programsNewCollection,
  selectAvailablePrograms,
  selectEnrolledPrograms,
} from '@/reducers/programsNew'
import { useAppSelector } from '@/hooks'
import {
  getDeviceVerificationStatus,
  VerificationStatusTagType,
} from '@/hooks/useDeviceVerificationStatus/useDeviceVerificationStatus'
import { match } from 'ts-pattern'

export const useDeviceProgramCategories = (
  deviceId: number | undefined,
  deviceType: DeviceType,
  config?: { multiProgramOnly?: boolean },
) => {
  const { isLoadingOrIdle } = programsNewCollection.useFetch()
  const availablePrograms = useAppSelector(
    selectAvailablePrograms(config?.multiProgramOnly),
  )
  const enrolledPrograms = useAppSelector(
    selectEnrolledPrograms(config?.multiProgramOnly),
  )
  const allPrograms = [...availablePrograms, ...enrolledPrograms]

  const enrollablePrograms: MappedUtilityProgram[] = []
  const eligiblePrograms: MappedUtilityProgram[] = []
  const deviceEnrolledPrograms: MappedUtilityProgram[] = []
  const ineligiblePrograms: MappedUtilityProgram[] = []
  const pendingPrograms: MappedUtilityProgram[] = []
  const completedPrograms: MappedUtilityProgram[] = []

  if (deviceId) {
    for (const program of allPrograms) {
      const ineligibilityMessages = getDeviceIneligiblityMessages(
        program.device_eligibility_overview.devices,
      )
      const isDeviceAllowed = isDeviceAllowedInProgram(
        deviceId,
        deviceType,
        program.device_eligibility_overview,
        ineligibilityMessages,
      )

      if (!isDeviceAllowed) {
        ineligiblePrograms.push(program)
        continue
      }

      const verificationStatus = getDeviceVerificationStatus(
        program.device_eligibility_overview,
        program.enrollment_overview,
        program.active,
        deviceId,
        deviceType,
      )?.deviceStatus

      const programList = match(verificationStatus)
        .with(VerificationStatusTagType.AVAILABLE, () => enrollablePrograms)
        .with(VerificationStatusTagType.ELIGIBLE, () => eligiblePrograms)
        .with(VerificationStatusTagType.PENDING, () => pendingPrograms)
        .with(VerificationStatusTagType.INELIGIBLE, () => ineligiblePrograms)
        .with(VerificationStatusTagType.ENROLLED, () => deviceEnrolledPrograms)
        .with(VerificationStatusTagType.COMPLETED, () => completedPrograms)
        .with(VerificationStatusTagType.INACTIVE, () => completedPrograms)
        .exhaustive()

      programList.push(program)
    }
  }

  return {
    enrollablePrograms,
    eligiblePrograms,
    deviceEnrolledPrograms,
    ineligiblePrograms,
    pendingPrograms,
    availablePrograms: [...enrollablePrograms, ...eligiblePrograms],
    completedPrograms,
    isLoading: isLoadingOrIdle,
  }
}

type Props = {
  showProgramApplications?: boolean
}

type PathParams = {
  deviceId?: string
  deviceType?: DeviceUrlSlug
}

const DeviceProgramCards = (props: Props) => {
  const { showProgramApplications = true } = props
  const { deviceId, deviceType } = useParams<PathParams>()
  const { allLegacyPrograms } = useUtilityPrograms({ onlyMultiPrograms: false })
  const {
    availablePrograms,
    pendingPrograms,
    ineligiblePrograms,
    isLoading,
    deviceEnrolledPrograms: enrolledPrograms,
    completedPrograms,
  } = useDeviceProgramCategories(
    deviceId ? parseInt(deviceId) : undefined,
    deviceType ? deviceTypeFromUrlSlug(deviceType) : 'home',
  )

  const loadingContent = (
    <Card>
      <div className="flex flex-col gap-4">
        <Text variant="h3">Program Enrollments</Text>
        <Text variant="body2">Looking up the programs for this device...</Text>
        <LoadingAnimation type="falling" centered />
      </div>
    </Card>
  )

  const otherDropdownsPopulated =
    enrolledPrograms.length !== 0 || // This lengthy boolean is to show the "no available programs CTA"
    ineligiblePrograms.length !== 0 || // as expanded iff there are no other programs to show
    pendingPrograms.length !== 0

  const hasLegacyPrograms = allLegacyPrograms?.length > 0

  const programApplicationsExist =
    enrolledPrograms.length > 0 ||
    pendingPrograms.length > 0 ||
    ineligiblePrograms.length > 0 ||
    completedPrograms.length > 0
  const programApplicationSection = programApplicationsExist &&
    showProgramApplications &&
    !hasLegacyPrograms && (
      <div className="flex flex-col gap-4 my-3">
        <Text variant="h3">All programs</Text>
        {availablePrograms.map((program) => (
          <ProgramApplicationCard key={program.id} program={program} />
        ))}
        {enrolledPrograms.map((program) => (
          <ProgramApplicationCard key={program.id} program={program} />
        ))}
        {pendingPrograms.map((program) => (
          <ProgramApplicationCard key={program.id} program={program} />
        ))}
        {ineligiblePrograms.map((program) => (
          <ProgramApplicationCard key={program.id} program={program} />
        ))}
        {completedPrograms.map((program) => (
          <ProgramApplicationCard key={program.id} program={program} />
        ))}
      </div>
    )

  const loadedContent = (
    <MultiProgramDeviceFlowProvider>
      <div className="flex flex-col gap-4">
        <ProgramCardAccordion
          title={`Available Programs (${availablePrograms.length})`}
          programs={availablePrograms}
          initialState={
            availablePrograms.length > 0 || !otherDropdownsPopulated
              ? 'open'
              : 'closed'
          }
          show={!hasLegacyPrograms}
          enrolled={false}
        />
        {/* The program applications section contain all the program cards outside of the available programs section */}
        {programApplicationSection}
      </div>
    </MultiProgramDeviceFlowProvider>
  )

  return (
    <LoadingContainer
      isLoading={isLoading}
      loadedContent={loadedContent}
      loadingContent={loadingContent}
    />
  )
}
export default DeviceProgramCards
